import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { BackLink } from '../../../components/BackLink'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { DetailField } from '../../../components/DetailField'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { SectionTitle } from '../../../components/SectionCard'
import ShortText from '../../../components/ShortText'
import { PAYMENT_STATUS, StatusBadge } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import {
  buildAsaasPaymentUrl,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatPhone,
  useAppToast,
  useHasRole
} from '../../../helpers'
import { MANUAL_PAYMENT_TYPE_LABELS } from '../../../services/Contests/interfaces'
import { EventService } from '../../../services/Event/EventService'
import {
  EventContestOrderDetail,
  EventOrder,
  EventOrderDetail,
  EventOrderSourceOption,
  TerminalPaymentMethod
} from '../../../services/Event/types'
import PaymentProof from './tabs/PaymentProof'
import { deviceTypeLabels, PAYMENT_METHOD, TERMINAL_STATUS, voidReasonLabels } from './tabs/tabLabels'

const paymentMethodLabels: Record<string, string> = {
  pix: 'PIX',
  credit_card: 'Cartão de Crédito',
  // Contest registration paid at the reception desk (ABRA-398)
  manual: 'Manual'
}

/**
 * Uma compra na porta (ABRA-319) é paga na maquininha ou no celular, então o método é o do terminal —
 * o mesmo `pix` é "PIX" no site (Asaas) e "PIX (Stone)" na porta.
 */
function paymentMethodLabel(order: EventOrder): string {
  if (!order.payment_method) return '-'
  if (order.source === 'door') {
    return PAYMENT_METHOD[order.payment_method as TerminalPaymentMethod]?.label ?? order.payment_method
  }
  return paymentMethodLabels[order.payment_method] ?? order.payment_method
}

/** Na porta, `refunded` é o estorno feito na maquininha — não um reembolso do Asaas. */
const DOOR_PAYMENT_STATUS = { ...PAYMENT_STATUS, refunded: { ...PAYMENT_STATUS.refunded, label: 'Estornado' } }

const paymentStatuses = (order: EventOrder) => (order.source === 'door' ? DOOR_PAYMENT_STATUS : PAYMENT_STATUS)

// A row is either an event order or a contest application shown as one (ABRA-233)
type SelectedOrder = EventOrderDetail | EventContestOrderDetail

const isContestOrder = (order: SelectedOrder): order is EventContestOrderDetail => order.source === 'contest'

// 'declined' is a contest-only status, terminal like the others
const terminalStatuses = ['cancelled', 'refunded', 'cancel_pending', 'refund_pending', 'declined']

/** Value of the origin filter: 'all', 'event', 'door' or 'contest:{id}'. */
const ALL_ORIGINS = 'all'

function parseOriginFilter(value: string): { origin?: string; contest_id?: number } {
  if (value === ALL_ORIGINS) return {}
  if (value === 'event') return { origin: 'event' }
  if (value === 'door') return { origin: 'door' }
  return { origin: 'contest', contest_id: Number(value.split(':')[1]) }
}

export default function EventOrdersPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const toast = useAppToast()
  const service = new EventService()

  const [eventId, setEventId] = useState<number | null>(null)
  const [orders, setOrders] = useState<EventOrder[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [originFilter, setOriginFilter] = useState(ALL_ORIGINS)
  const [sources, setSources] = useState<EventOrderSourceOption[]>([])
  /** Vendas na porta do evento (ABRA-319): com alguma, o filtro de origem ganha "Porta". */
  const [doorSalesCount, setDoorSalesCount] = useState(0)

  const [selectedOrder, setSelectedOrder] = useState<SelectedOrder | null>(null)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure()

  const [isCancelling, setIsCancelling] = useState(false)
  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onAlertClose } = useDisclosure()

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // Resolve event id from slug once
  useEffect(() => {
    service
      .getBySlug(slug!)
      .then((data) => {
        setEventId(data.id)
        setDoorSalesCount(data.door_sales_count ?? 0)
        // Contests linked to the event feed the origin filter; failing to load them
        // only hides the filter, the listing still works.
        service
          .getOrderSources(data.id)
          .then(setSources)
          .catch(() => undefined)
      })
      .catch(() => {
        toast.error('Evento não encontrado')
        navigate('/events')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  useEffect(() => {
    if (eventId === null) return
    const timeout = setTimeout(fetchOrders, 400)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, search, statusFilter, originFilter, page, pageSize])

  function fetchOrders() {
    if (eventId === null) return
    setLoading(true)
    setLoadError(false)
    service
      .getOrders(eventId, {
        search: search || undefined,
        payment_status: statusFilter || undefined,
        ...parseOriginFilter(originFilter),
        page,
        page_size: pageSize
      })
      .then((res) => {
        setOrders(res.data)
        setTotal(res.total)
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar as compras', error)
      })
      .finally(() => setLoading(false))
  }

  function handleRowClick(order: EventOrder) {
    if (eventId === null) return
    // Drop the previous order so the drawer header does not keep showing its uuid while loading
    setSelectedOrder(null)
    setDrawerLoading(true)
    onDrawerOpen()
    const request =
      order.source === 'contest'
        ? service.getContestOrderDetail(eventId, order.order_uuid)
        : service.getOrderDetail(eventId, order.order_uuid)
    request
      .then((detail) => setSelectedOrder(detail))
      .catch(() => {
        toast.error('Erro ao carregar detalhes da compra')
        onDrawerClose()
      })
      .finally(() => setDrawerLoading(false))
  }

  // A mesma confirmação serve dois desfechos: pedido pago pelo Asaas vira estorno,
  // o resto vira cancelamento. O predicado sai daqui para não repetir a condição.
  const isRefundableOrder = selectedOrder?.payment_status === 'paid' && Boolean(selectedOrder?.asaas_payment_id)

  function handleCancelConfirm() {
    if (!selectedOrder || eventId === null) return
    setIsCancelling(true)
    const request = isContestOrder(selectedOrder)
      ? service.cancelContestOrder(eventId, selectedOrder.order_uuid)
      : service.cancelOrder(eventId, selectedOrder.order_uuid)
    request
      .then((res) => {
        const isRefund = res.payment_status === 'refunded' || res.payment_status === 'refund_pending'
        const isPending = res.payment_status === 'cancel_pending' || res.payment_status === 'refund_pending'
        toast.success(
          isRefund ? 'Estorno solicitado' : 'Cancelamento solicitado',
          isPending ? `${res.message} Aguarde a confirmação.` : res.message
        )
        onAlertClose()
        onDrawerClose()
        setSelectedOrder(null)
        fetchOrders()
      })
      .catch((err) => {
        const msg = err?.response?.data?.error ?? 'Erro ao cancelar compra'
        toast.error(msg)
      })
      .finally(() => setIsCancelling(false))
  }

  const totalPages = Math.ceil(total / pageSize)
  const hasRole = useHasRole()

  // Uma compra na porta só é desfeita pelo estorno na maquininha Stone (ABRA-319).
  const isDoorOrder = selectedOrder?.source === 'door'
  const doorSale = selectedOrder && !isContestOrder(selectedOrder) ? selectedOrder.door_sale : null
  const canCancel = selectedOrder && !isDoorOrder && !terminalStatuses.includes(selectedOrder.payment_status)

  return (
    <>
      {/* Top nav */}
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={4}
        mb={5}
      >
        <BackLink to={`/events/${slug}`}>Voltar para o Evento</BackLink>
      </Flex>

      <PageTitle title="Compras do Evento" description="Ingressos e inscrições vendidos para o evento" mb={5} />

      <FilterBar
        onClear={() => {
          setSearch('')
          setStatusFilter('')
          setOriginFilter(ALL_ORIGINS)
          setPage(1)
        }}
        isDirty={search !== '' || statusFilter !== '' || originFilter !== ALL_ORIGINS}
      >
        <FilterSearch
          value={search}
          onChange={(value) => {
            setSearch(value)
            setPage(1)
          }}
          placeholder="Buscar por nome ou e-mail..."
        />
        <FilterSelect
          label="Status"
          maxW={{ md: '220px' }}
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value)
            setPage(1)
          }}
          options={Object.entries(PAYMENT_STATUS).map(([value, { label }]) => ({ value, label }))}
        />
        {(sources.length > 0 || doorSalesCount > 0) && (
          <FilterSelect
            label="Origem"
            maxW={{ md: '240px' }}
            value={originFilter}
            onChange={(value) => {
              setOriginFilter(value)
              setPage(1)
            }}
            options={[
              // Com vendas na porta, "do evento" deixaria de dizer qual: as da porta também são.
              { value: 'event', label: doorSalesCount > 0 ? 'Somente do site' : 'Somente do evento' },
              ...(doorSalesCount > 0 ? [{ value: 'door', label: 'Porta' }] : []),
              ...sources.map((contest) => ({ value: `contest:${contest.id}`, label: contest.name }))
            ]}
            emptyLabel="Todas as origens"
            emptyValue={ALL_ORIGINS}
          />
        )}
      </FilterBar>

      <TableContainer padding="0" mt="2">
        <Table>
          <Thead>
            <Tr>
              <Th>Comprador</Th>
              <Th>E-mail</Th>
              <Th>Origem</Th>
              <Th>Método</Th>
              <Th>Cupom</Th>
              <Th>Status</Th>
              <Th isNumeric>Total</Th>
              <Th isNumeric>Ingressos</Th>
              <Th>Data</Th>
              <Th>
                UTM Source{' '}
                <Tooltip label="Identifica de onde veio o tráfego (o site, motor de busca ou plataforma).">
                  <Icon name="lni lni-information" cursor="help" color="blue.300" size="md" />
                </Tooltip>
              </Th>
              <Th>
                UTM Medium{' '}
                <Tooltip label="Identifica o tipo de canal ou meio de divulgação utilizado.">
                  <Icon name="lni lni-information" cursor="help" color="blue.300" size="md" />
                </Tooltip>
              </Th>
              <Th>
                UTM Campaign{' '}
                <Tooltip label="Identifica a campanha específica, promoção ou objetivo estratégico.">
                  <Icon name="lni lni-information" cursor="help" color="blue.300" size="md" />
                </Tooltip>
              </Th>
              <Th>
                UTM Content{' '}
                <Tooltip label="Usado para diferenciar anúncios ou links semelhantes no mesmo local, ideal para testes A/B.">
                  <Icon name="lni lni-information" cursor="help" color="blue.300" size="md" />
                </Tooltip>
              </Th>
              <Th>
                UTM Term{' '}
                <Tooltip label="Usado principalmente para identificar palavras-chave em anúncios pagos (como Google Ads).">
                  <Icon name="lni lni-information" cursor="help" color="blue.300" size="md" />
                </Tooltip>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={14} rows={8} />
            ) : loadError ? (
              <TableErrorRow colSpan={14} entity="compras" feminine onRetry={fetchOrders} />
            ) : orders.length === 0 ? (
              <TableEmptyRow colSpan={14} entity="compra" feminine />
            ) : (
              orders.map((order) => {
                return (
                  <Tr
                    key={order.order_uuid}
                    role="button"
                    tabIndex={0}
                    _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                    onClick={() => handleRowClick(order)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handleRowClick(order)
                      }
                    }}
                  >
                    <Td>
                      <Text fontSize="sm" fontWeight="500" color="gray.800">
                        {order.buyer_name}
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="caption">{order.buyer_email}</Text>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={order.source === 'contest' ? 'teal' : order.source === 'door' ? 'cyan' : 'gray'}
                      >
                        {order.source_label}
                      </Badge>
                    </Td>
                    <Td>
                      <Text variant="caption">{paymentMethodLabel(order)}</Text>
                    </Td>
                    <Td>
                      <EmptyCell variant="caption" value={order.coupon_code} />
                    </Td>
                    <Td>
                      <StatusBadge status={order.payment_status} statuses={paymentStatuses(order)} />
                    </Td>
                    <Td isNumeric>
                      <Text fontSize="sm" color="gray.700">
                        {order.total_paid === 0 ? 'Gratuito' : formatCurrency(order.total_paid)}
                      </Text>
                    </Td>
                    <Td isNumeric>
                      <Text variant="caption">{order.ticket_count}</Text>
                    </Td>
                    <Td>
                      <Text variant="caption">{formatDate(order.created_at)}</Text>
                    </Td>
                    <Td>
                      <EmptyCell variant="caption" value={order.utm_source} />
                    </Td>
                    <Td>
                      <EmptyCell variant="caption" value={order.utm_medium} />
                    </Td>
                    <Td>
                      <Text variant="caption">
                        {order.utm_campaign ? (
                          <ShortText text={order.utm_campaign} beforeChars={20} afterChars={0} />
                        ) : (
                          '—'
                        )}
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="caption">
                        {order.utm_content ? (
                          <ShortText text={order.utm_content} beforeChars={20} afterChars={0} />
                        ) : (
                          '—'
                        )}
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="caption">
                        {order.utm_term ? (
                          <ShortText text={order.utm_term} beforeChars={20} afterChars={0} />
                        ) : (
                          <EmptyCell />
                        )}
                      </Text>
                    </Td>
                  </Tr>
                )
              })
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Paginator
          page={page}
          totalPages={totalPages}
          changePage={setPage}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
        />
      )}

      {/* Order Detail Drawer */}
      <Drawer isOpen={isDrawerOpen} placement="right" onClose={onDrawerClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            {/* `h2` porque os blocos da gaveta são `SectionTitle variant="mini"`, que emite `h3`:
                sem este nível o outline pularia do `h1` da página direto para eles. */}
            <Heading as="h2" fontWeight="700">
              Detalhes da Compra
            </Heading>
            {selectedOrder && (
              <Text variant="caption" mt={0.5}>
                #{selectedOrder.order_uuid}
              </Text>
            )}
          </DrawerHeader>

          <DrawerBody>
            {drawerLoading || !selectedOrder ? (
              <VStack spacing={3} pt={4}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} height="20px" w="100%" />
                ))}
              </VStack>
            ) : (
              <VStack align="stretch" spacing={5} pt={2}>
                {/* Buyer info */}
                <Box>
                  <SectionTitle variant="mini">Comprador</SectionTitle>
                  <VStack align="stretch" spacing={1}>
                    <DetailField layout="inline" label="Nome" value={selectedOrder.buyer_name} />
                    <DetailField layout="inline" label="E-mail" value={selectedOrder.buyer_email} />
                    {selectedOrder.buyer_phone && (
                      <DetailField
                        layout="inline"
                        label="Telefone"
                        value={formatPhone(selectedOrder.buyer_phone) ?? selectedOrder.buyer_phone}
                      />
                    )}
                  </VStack>
                </Box>

                {/* Payment info */}
                <Box>
                  <SectionTitle variant="mini">Pagamento</SectionTitle>
                  <VStack align="stretch" spacing={1}>
                    <DetailField layout="inline" label="Status">
                      <StatusBadge status={selectedOrder.payment_status} statuses={paymentStatuses(selectedOrder)} />
                    </DetailField>
                    <DetailField layout="inline" label="Método" value={paymentMethodLabel(selectedOrder)} />
                    <DetailField layout="inline" label="Cupom" value={selectedOrder.coupon_code} />
                    <DetailField
                      layout="inline"
                      label="Total"
                      value={selectedOrder.total_paid === 0 ? 'Gratuito' : formatCurrency(selectedOrder.total_paid)}
                    />
                    <DetailField layout="inline" label="Data" value={formatDate(selectedOrder.created_at)} />
                    {/* Contest registration paid at the reception desk (ABRA-398) */}
                    {isContestOrder(selectedOrder) && selectedOrder.manual_payment_type && (
                      <>
                        <DetailField
                          layout="inline"
                          label="Forma na recepção"
                          value={
                            MANUAL_PAYMENT_TYPE_LABELS[selectedOrder.manual_payment_type] ??
                            selectedOrder.manual_payment_type
                          }
                        />
                        {selectedOrder.payment_notes && (
                          <DetailField layout="inline" label="Observação" value={selectedOrder.payment_notes} />
                        )}
                        {selectedOrder.created_by_name && (
                          <DetailField layout="inline" label="Registrada por" value={selectedOrder.created_by_name} />
                        )}
                      </>
                    )}
                  </VStack>
                  {selectedOrder.asaas_payment_id && (
                    <Button
                      as="a"
                      href={buildAsaasPaymentUrl(selectedOrder.asaas_payment_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      mt={3}
                      size="sm"
                      colorScheme="purple"
                      variant="outline"
                      w="100%"
                      leftIcon={<Icon name="lni lni-link" size="md" />}
                    >
                      Abrir cobrança no Asaas
                    </Button>
                  )}
                </Box>

                {/* Door sale (ABRA-319) */}
                {doorSale && (
                  <Box>
                    <SectionTitle variant="mini">Venda na porta</SectionTitle>
                    <VStack align="stretch" spacing={1}>
                      <DetailField
                        layout="inline"
                        label="Origem"
                        value={doorSale.is_manual ? 'Pagamento manual (celular)' : 'Stone (terminal)'}
                      />
                      <DetailField layout="inline" label="Situação">
                        <StatusBadge status={doorSale.status} statuses={TERMINAL_STATUS} />
                      </DetailField>
                      {(doorSale.method === 'credit' || doorSale.method === 'credit_manual') && (
                        <DetailField
                          layout="inline"
                          label="Parcelas"
                          value={`${doorSale.installments}x${doorSale.has_interest ? ' (com juros)' : ''}`}
                        />
                      )}
                      {doorSale.external_reference && (
                        <DetailField layout="inline" label="ATK Stone" value={doorSale.external_reference} />
                      )}
                      <DetailField layout="inline" label="Valor cobrado" value={formatCurrency(doorSale.amount_paid)} />
                      <DetailField
                        layout="inline"
                        label="Máquina"
                        value={
                          doorSale.machine
                            ? `${doorSale.machine.name} · ${
                                deviceTypeLabels[doorSale.machine.device_type] ?? doorSale.machine.device_type
                              }`
                            : null
                        }
                      />
                      {doorSale.payment_method_note && (
                        <DetailField layout="inline" label="Observação" value={doorSale.payment_method_note} />
                      )}
                      {doorSale.status === 'voided' && (
                        <>
                          <DetailField
                            layout="inline"
                            label="Estornado em"
                            value={
                              doorSale.voided_at ? formatDateTime(doorSale.voided_at, { showSeconds: true }) : null
                            }
                          />
                          {doorSale.void_reason && (
                            <DetailField
                              layout="inline"
                              label="Motivo"
                              value={voidReasonLabels[doorSale.void_reason] ?? doorSale.void_reason}
                            />
                          )}
                          {doorSale.voided_by_machine && (
                            <DetailField
                              layout="inline"
                              label="Estornado por"
                              value={doorSale.voided_by_machine.name}
                            />
                          )}
                        </>
                      )}
                    </VStack>
                    {doorSale.exceeded_stock && (
                      <Text fontSize="xs" color="orange.600" mt={2}>
                        O site vendeu as últimas unidades enquanto esta venda era cobrada: ela passou do estoque do
                        ingresso.
                      </Text>
                    )}
                    {doorSale.proof_image_url && (
                      <Box mt={3}>
                        <PaymentProof url={doorSale.proof_image_url} />
                      </Box>
                    )}
                    <Text variant="caption" mt={3}>
                      {doorSale.is_manual
                        ? 'Venda na porta paga por fora (dinheiro, PIX ou outra maquininha): não tem estorno pelo sistema, a devolução é feita por fora.'
                        : 'Venda na porta não é cancelada pelo painel: o estorno é feito só na maquininha Stone, antes do check-in.'}
                    </Text>
                  </Box>
                )}

                {/* Contest registration (ABRA-233) */}
                {isContestOrder(selectedOrder) && (
                  <Box>
                    <SectionTitle variant="mini">Inscrição</SectionTitle>
                    <VStack align="stretch" spacing={1}>
                      <DetailField layout="inline" label="Concurso" value={selectedOrder.contest.name} />
                      {selectedOrder.applicant_cpf && (
                        <DetailField layout="inline" label="CPF" value={selectedOrder.applicant_cpf} />
                      )}
                      {selectedOrder.course_name && (
                        <DetailField layout="inline" label="Curso" value={selectedOrder.course_name} />
                      )}
                      {selectedOrder.teacher_name && (
                        <DetailField layout="inline" label="Professor" value={selectedOrder.teacher_name} />
                      )}
                      <DetailField
                        layout="inline"
                        label="Obras submetidas"
                        value={String(selectedOrder.art_submissions_count)}
                      />
                    </VStack>
                    {hasRole('view_contests_applications') && (
                      <Button
                        as={Link}
                        to={`/contests/applications?contest=${selectedOrder.contest.slug}`}
                        mt={3}
                        size="sm"
                        colorScheme="teal"
                        variant="outline"
                        w="100%"
                        leftIcon={<Icon name="lni lni-eye" size="md" />}
                      >
                        Ver inscrição completa
                      </Button>
                    )}
                  </Box>
                )}

                {/* Participants */}
                {!isContestOrder(selectedOrder) && (
                  <Box>
                    <SectionTitle variant="mini" mb={3}>
                      Participantes ({selectedOrder.participants.length})
                    </SectionTitle>
                    <VStack align="stretch" spacing={3}>
                      {selectedOrder.participants.map((p) => (
                        <Box key={p.id} p={3} borderRadius="lg" border="1px" borderColor="gray.100" bg="gray.50">
                          <Flex justify="space-between" align="flex-start">
                            <Box>
                              <Text fontSize="sm" fontWeight="600" color="gray.800">
                                {p.name}
                              </Text>
                              <Text variant="caption" mt={0.5}>
                                {p.email}
                              </Text>
                            </Box>
                            <Flex gap={1} flexShrink={0} ml={2} align="center">
                              {p.ticket && (
                                <Badge
                                  colorScheme={
                                    p.ticket.type === 'FREE' && p.ticket.price > 0
                                      ? 'orange'
                                      : p.ticket.type === 'FREE'
                                        ? 'green'
                                        : 'purple'
                                  }
                                  fontSize="10px"
                                  borderRadius="md"
                                  px={1.5}
                                >
                                  {p.ticket.title}
                                </Badge>
                              )}
                              {p.ticket?.is_child_ticket && <Badge colorScheme="purple">Infantil</Badge>}
                              {p.ticket && p.ticket.type === 'FREE' && p.ticket.price > 0 && (
                                <Tooltip
                                  label={
                                    p.has_checkin
                                      ? 'Check-in realizado — elegível para reembolso'
                                      : 'Caução reembolsável após check-in no evento'
                                  }
                                >
                                  <Badge colorScheme={p.has_checkin ? 'blue' : 'orange'}>
                                    {p.has_checkin ? 'Check-in ✓' : 'Caução'}
                                  </Badge>
                                </Tooltip>
                              )}
                            </Flex>
                          </Flex>
                          <Flex gap={4} mt={2}>
                            <Text variant="caption">
                              Valor: <strong>{p.paid_value === 0 ? 'Gratuito' : formatCurrency(p.paid_value)}</strong>
                            </Text>
                            {p.discount_value > 0 && (
                              <Text fontSize="xs" color="green.500">
                                Desconto: {formatCurrency(p.discount_value)}
                              </Text>
                            )}
                          </Flex>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Cancel action */}
                {canCancel &&
                  (() => {
                    // A manual contest payment has no Asaas charge but is still paid (ABRA-398), so
                    // contest orders follow the API's is_free instead of the charge id
                    const isPaidOrder = isContestOrder(selectedOrder)
                      ? !selectedOrder.is_free
                      : !!selectedOrder.asaas_payment_id
                    // Contest purchases accept either the event or the contest permission (ABRA-233)
                    const hasCancelPermission = isContestOrder(selectedOrder)
                      ? hasRole(
                          isPaidOrder
                            ? ['cancel_ticket_event', 'cancel_ticket_contests']
                            : ['cancel_free_ticket_event', 'cancel_free_ticket_contests']
                        )
                      : hasRole(isPaidOrder ? 'cancel_ticket_event' : 'cancel_free_ticket_event')
                    return (
                      <Box pt={2}>
                        <Tooltip
                          label={
                            isPaidOrder
                              ? 'Sem permissão para cancelar tickets pagos'
                              : 'Sem permissão para cancelar tickets gratuitos'
                          }
                          isDisabled={hasCancelPermission}
                        >
                          <span>
                            <Button
                              colorScheme="red"
                              variant="outline"
                              size="sm"
                              w="100%"
                              isDisabled={!hasCancelPermission}
                              leftIcon={<Icon name="lni lni-close" size="md" />}
                              onClick={onAlertOpen}
                            >
                              {selectedOrder.payment_status === 'paid' && selectedOrder.asaas_payment_id
                                ? 'Solicitar Reembolso'
                                : 'Cancelar Compra'}
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    )
                  })()}
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Cancel / Refund Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isAlertOpen}
        onClose={onAlertClose}
        onConfirm={handleCancelConfirm}
        action={isRefundableOrder ? 'refund' : 'delete'}
        title={isRefundableOrder ? 'Solicitar reembolso' : 'Cancelar compra'}
        description={
          isRefundableOrder ? (
            <>
              Deseja solicitar o estorno integral desta compra via Asaas?
              <br />
              <Text as="span" fontWeight="600">
                O estorno será processado pelo Asaas. O status será atualizado automaticamente quando confirmado.
              </Text>
            </>
          ) : (
            'Deseja cancelar esta compra? Esta ação não pode ser desfeita.'
          )
        }
        confirmLabel={isRefundableOrder ? 'Confirmar reembolso' : 'Confirmar cancelamento'}
        cancelLabel="Voltar"
        isLoading={isCancelling}
      />
    </>
  )
}
